import React, { useEffect, useRef, useState } from 'react';
import type { CurationCandidate } from '../api/types';
import { STUDIO_COPY } from '../config';
import {
  clampBounds,
  clampMusicFade,
  excitementHeat,
  formatClipSec,
  formatTotalClock,
  playTypeChip,
  reorderIds,
  trimRails,
  type ClipBounds,
} from './customizeHelpers';

const MIN_PX_PER_SEC = 18;
const NUDGE_SEC = 0.5;
const DRAG_THRESHOLD_PX = 6;
const PLAYHEAD_STEP = 0.02;

type CustomizeTimelineProps = {
  orderedIds: string[];
  byId: Map<string, CurationCandidate>;
  bounds: Record<string, ClipBounds>;
  activeId: string | null;
  disabled?: boolean;
  onReorder: (next: string[]) => void;
  onBoundsChange: (id: string, next: ClipBounds) => void;
  onSelectClip: (id: string) => void;
  /** When true, render the music filmstrip lane under the clip track. */
  musicEnabled?: boolean;
  /** Display name for the selected track; null/empty → Add music affordance. */
  musicLabel?: string | null;
  onMusicLaneClick?: () => void;
  /** V2: stack Clips / Music / Voice lanes with a shared playhead. */
  stackedLanes?: boolean;
  voiceLabel?: string | null;
  playheadRatio?: number;
  onPlayheadSeek?: (ratio: number) => void;
  musicFadeInS?: number;
  musicFadeOutS?: number;
  onMusicFadeChange?: (next: {
    music_fade_in_s?: number;
    music_fade_out_s?: number;
  }) => void;
};

type DragMode =
  | {
      kind: 'reorder';
      fromIndex: number;
      overIndex: number;
      originX: number;
      originY: number;
      moved: boolean;
    }
  | {
      kind: 'trim';
      id: string;
      edge: 'start' | 'end';
      originX: number;
      origin: ClipBounds;
    }
  | {
      kind: 'fade';
      edge: 'in' | 'out';
      originX: number;
      originIn: number;
      originOut: number;
    }
  | {
      kind: 'playhead';
    }
  | null;

const CustomizeTimeline: React.FC<CustomizeTimelineProps> = ({
  orderedIds,
  byId,
  bounds,
  activeId,
  disabled = false,
  onReorder,
  onBoundsChange,
  onSelectClip,
  musicEnabled = false,
  musicLabel = null,
  onMusicLaneClick,
  stackedLanes = false,
  voiceLabel = null,
  playheadRatio = 0,
  onPlayheadSeek,
  musicFadeInS = 0,
  musicFadeOutS = 0,
  onMusicFadeChange,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const scrubRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragMode>(null);
  const orderedIdsRef = useRef(orderedIds);
  const byIdRef = useRef(byId);
  const disabledRef = useRef(disabled);
  const onReorderRef = useRef(onReorder);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onPlayheadSeekRef = useRef(onPlayheadSeek);
  const onMusicFadeChangeRef = useRef(onMusicFadeChange);
  const pxPerSec = Math.max(MIN_PX_PER_SEC, 28);
  const pxPerSecRef = useRef(pxPerSec);
  const [drag, setDrag] = useState<DragMode>(null);
  const [edgeHit, setEdgeHit] = useState<'start' | 'end' | null>(null);
  const suppressClickRef = useRef(false);
  const listenersBoundRef = useRef(false);

  // Stable window handlers (identity never changes) so bind/unbind always match.
  const onWindowMoveRef = useRef<(event: { clientX: number; clientY: number }) => void>(
    () => undefined
  );
  const onWindowUpRef = useRef<() => void>(() => undefined);
  const stableMoveRef = useRef((event: PointerEvent) => {
    onWindowMoveRef.current(event);
  });
  const stableUpRef = useRef(() => {
    onWindowUpRef.current();
  });

  orderedIdsRef.current = orderedIds;
  byIdRef.current = byId;
  disabledRef.current = disabled;
  onReorderRef.current = onReorder;
  onBoundsChangeRef.current = onBoundsChange;
  onPlayheadSeekRef.current = onPlayheadSeek;
  onMusicFadeChangeRef.current = onMusicFadeChange;
  pxPerSecRef.current = pxPerSec;

  const totalDuration = orderedIds.reduce((sum, id) => {
    const b = bounds[id];
    return sum + (b ? Math.max(0, b.end - b.start) : 0);
  }, 0);
  const totalDurationRef = useRef(totalDuration);
  totalDurationRef.current = totalDuration;

  const clampedPlayhead = Math.min(1, Math.max(0, playheadRatio));

  const seekFromClientX = (clientX: number) => {
    const rail = scrubRef.current || trackRef.current;
    if (!rail || !onPlayheadSeekRef.current) return;
    const rect = rail.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onPlayheadSeekRef.current(ratio);
  };

  const moveClip = (fromIndex: number, delta: number) => {
    const toIndex = fromIndex + delta;
    if (toIndex < 0 || toIndex >= orderedIds.length) return;
    onReorder(reorderIds(orderedIds, fromIndex, toIndex));
  };

  onWindowMoveRef.current = (event: { clientX: number; clientY: number }) => {
    const current = dragRef.current;
    if (!current || disabledRef.current) return;

    if (current.kind === 'playhead') {
      seekFromClientX(event.clientX);
      return;
    }

    if (current.kind === 'fade') {
      const deltaSec =
        (event.clientX - current.originX) / pxPerSecRef.current;
      const total = totalDurationRef.current;
      if (current.edge === 'in') {
        onMusicFadeChangeRef.current?.({
          music_fade_in_s: clampMusicFade(
            current.originIn + deltaSec,
            current.originOut,
            total
          ),
        });
      } else {
        onMusicFadeChangeRef.current?.({
          music_fade_out_s: clampMusicFade(
            current.originOut - deltaSec,
            current.originIn,
            total
          ),
        });
      }
      return;
    }

    if (current.kind === 'reorder') {
      const dx = event.clientX - current.originX;
      const dy = event.clientY - current.originY;
      const track = trackRef.current;
      let overIndex = current.fromIndex;
      if (track) {
        const children = Array.from(
          track.querySelectorAll<HTMLElement>('[data-timeline-id]')
        );
        for (let i = 0; i < children.length; i += 1) {
          const rect = children[i].getBoundingClientRect();
          const mid = rect.left + rect.width / 2;
          if (event.clientX < mid) {
            overIndex = i;
            break;
          }
          overIndex = i;
        }
      }
      const moved =
        current.moved ||
        Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX ||
        overIndex !== current.fromIndex;
      if (overIndex !== current.overIndex || moved !== current.moved) {
        const next = { ...current, overIndex, moved };
        dragRef.current = next;
        setDrag(next);
      }
      return;
    }

    if (current.kind === 'trim') {
      const cand = byIdRef.current.get(current.id);
      if (!cand) return;
      const deltaSec =
        (event.clientX - current.originX) / pxPerSecRef.current;
      const rails = trimRails(cand);
      let start = current.origin.start;
      let end = current.origin.end;
      if (current.edge === 'start') {
        start = current.origin.start + deltaSec;
      } else {
        end = current.origin.end + deltaSec;
      }
      const next = clampBounds(rails, start, end);
      const hitStart = Math.abs(next.start - rails.start) < 0.05;
      const hitEnd = Math.abs(next.end - rails.end) < 0.05;
      setEdgeHit(
        hitStart && current.edge === 'start'
          ? 'start'
          : hitEnd && current.edge === 'end'
            ? 'end'
            : null
      );
      onBoundsChangeRef.current(current.id, next);
    }
  };

  onWindowUpRef.current = () => {
    const current = dragRef.current;
    if (current?.kind === 'reorder') {
      if (current.overIndex !== current.fromIndex) {
        suppressClickRef.current = true;
        onReorderRef.current(
          reorderIds(
            orderedIdsRef.current,
            current.fromIndex,
            current.overIndex
          )
        );
      } else if (current.moved) {
        suppressClickRef.current = true;
      }
    } else if (
      current?.kind === 'trim' ||
      current?.kind === 'fade' ||
      current?.kind === 'playhead'
    ) {
      suppressClickRef.current = true;
    }
    dragRef.current = null;
    setDrag(null);
    setEdgeHit(null);
    if (listenersBoundRef.current) {
      listenersBoundRef.current = false;
      window.removeEventListener('pointermove', stableMoveRef.current);
      window.removeEventListener('pointerup', stableUpRef.current);
      window.removeEventListener('pointercancel', stableUpRef.current);
    }
  };

  const bindWindow = () => {
    if (listenersBoundRef.current) return;
    listenersBoundRef.current = true;
    window.addEventListener('pointermove', stableMoveRef.current);
    window.addEventListener('pointerup', stableUpRef.current);
    window.addEventListener('pointercancel', stableUpRef.current);
  };

  useEffect(() => {
    const move = stableMoveRef.current;
    const up = stableUpRef.current;
    return () => {
      if (listenersBoundRef.current) {
        listenersBoundRef.current = false;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
      }
    };
  }, []);

  const startDrag = (next: Exclude<DragMode, null>) => {
    dragRef.current = next;
    setDrag(next);
    bindWindow();
  };

  const nudge = (id: string, edge: 'start' | 'end', delta: number) => {
    const c = byId.get(id);
    const cur = bounds[id];
    if (!c || !cur) return;
    const rails = trimRails(c);
    const start = edge === 'start' ? cur.start + delta : cur.start;
    const end = edge === 'end' ? cur.end + delta : cur.end;
    onBoundsChange(id, clampBounds(rails, start, end));
  };

  const fadeInPct =
    totalDuration > 0 ? Math.min(50, (Math.max(0, musicFadeInS) / totalDuration) * 100) : 0;
  const fadeOutPct =
    totalDuration > 0 ? Math.min(50, (Math.max(0, musicFadeOutS) / totalDuration) * 100) : 0;
  const showFadeHandles = Boolean(
    stackedLanes && musicEnabled && musicLabel && onMusicFadeChange
  );

  return (
    <div
      className={`studio-customize-timeline${
        stackedLanes ? ' is-stacked' : ''
      }`}
      data-testid="studio-customize-timeline"
      data-stacked={stackedLanes ? 'true' : 'false'}
      data-dragging={drag ? drag.kind : 'idle'}
      data-playhead={clampedPlayhead.toFixed(4)}
      data-drag-meta={
        drag && drag.kind === 'reorder'
          ? `${drag.fromIndex}:${drag.overIndex}:${drag.moved ? '1' : '0'}`
          : 'none'
      }
      onPointerMove={(event) => {
        if (!dragRef.current) return;
        onWindowMoveRef.current({
          clientX: event.clientX,
          clientY: event.clientY,
        });
      }}
      onPointerUp={() => {
        if (!dragRef.current) return;
        onWindowUpRef.current();
      }}
      onPointerCancel={() => {
        if (!dragRef.current) return;
        onWindowUpRef.current();
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs text-[var(--studio-muted)]">
          {STUDIO_COPY.customizeStep2Hint}
        </p>
        <p
          className="text-sm font-medium tabular-nums shrink-0"
          data-testid="studio-customize-timeline-total"
        >
          {STUDIO_COPY.customizeTotalTemplate.replace(
            '{total}',
            formatTotalClock(totalDuration)
          )}
        </p>
      </div>

      <div className="studio-customize-timeline-scroll">
        <div className="studio-customize-timeline-stack">
          {stackedLanes ? (
            <span className="studio-customize-lane-label">
              {STUDIO_COPY.recapEditorV2ToolClips}
            </span>
          ) : null}
          <div
            className="studio-customize-timeline-playhead-wrap"
            style={
              {
                '--recap-v2-playhead': String(clampedPlayhead),
              } as React.CSSProperties
            }
          >
          {stackedLanes ? (
            <div
              ref={scrubRef}
              className="studio-customize-playhead-scrub"
              data-testid="studio-customize-playhead-scrub"
              role="slider"
              tabIndex={disabled ? -1 : 0}
              aria-label="Timeline playhead"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(clampedPlayhead * 100)}
              onPointerDown={(event) => {
                if (disabled || !onPlayheadSeek) return;
                event.preventDefault();
                startDrag({ kind: 'playhead' });
                seekFromClientX(event.clientX);
              }}
              onKeyDown={(event) => {
                if (!onPlayheadSeek || disabled) return;
                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                  event.preventDefault();
                  const delta = event.key === 'ArrowLeft' ? -PLAYHEAD_STEP : PLAYHEAD_STEP;
                  onPlayheadSeek(Math.min(1, Math.max(0, clampedPlayhead + delta)));
                }
              }}
            />
          ) : null}
          <div
            ref={trackRef}
            className="studio-customize-timeline-track"
            data-testid="studio-customize-timeline-track"
          >
            {orderedIds.map((id, index) => {
              const c = byId.get(id);
              const b = bounds[id];
              if (!c || !b) return null;
              const duration = Math.max(0.1, b.end - b.start);
              const width = Math.max(56, duration * pxPerSec);
              const rails = trimRails(c);
              const atStart = Math.abs(b.start - rails.start) < 0.05;
              const atEnd = Math.abs(b.end - rails.end) < 0.05;
              const isActive = activeId === id;
              const isDragging =
                drag?.kind === 'reorder' &&
                drag.moved &&
                drag.fromIndex === index;
              const isDropTarget =
                drag?.kind === 'reorder' &&
                drag.moved &&
                drag.overIndex === index &&
                drag.fromIndex !== index;
              const heat = stackedLanes ? excitementHeat(c) : null;
              const chip = stackedLanes ? playTypeChip(c) : null;

              return (
                <div
                  key={id}
                  data-timeline-id={id}
                  className={`studio-customize-timeline-clip ${
                    isActive ? 'is-active' : ''
                  } ${isDragging ? 'is-dragging' : ''} ${
                    isDropTarget ? 'is-drop-target' : ''
                  }`}
                  style={{ width }}
                  data-testid={`studio-customize-timeline-clip-${id}`}
                >
                  <button
                    type="button"
                    className="studio-customize-timeline-body"
                    disabled={disabled}
                    data-testid={`studio-customize-timeline-body-${id}`}
                    aria-keyshortcuts={stackedLanes ? 'ArrowLeft ArrowRight' : undefined}
                    onClick={() => {
                      if (suppressClickRef.current) {
                        suppressClickRef.current = false;
                        return;
                      }
                      onSelectClip(id);
                    }}
                    onKeyDown={(event) => {
                      if (!stackedLanes || disabled) return;
                      if (event.key === 'ArrowLeft') {
                        event.preventDefault();
                        event.stopPropagation();
                        moveClip(index, -1);
                      } else if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        event.stopPropagation();
                        moveClip(index, 1);
                      }
                    }}
                    onPointerDown={(event) => {
                      if (disabled) return;
                      event.preventDefault();
                      startDrag({
                        kind: 'reorder',
                        fromIndex: index,
                        overIndex: index,
                        originX: event.clientX,
                        originY: event.clientY,
                        moved: false,
                      });
                    }}
                  >
                    <span className="studio-customize-timeline-num">
                      {index + 1}
                    </span>
                    <span className="studio-customize-timeline-label truncate">
                      {c.label}
                    </span>
                    <span className="studio-customize-timeline-dur">
                      {formatClipSec(duration)}
                    </span>
                    {stackedLanes ? (
                      <span
                        className={`studio-customize-heat${
                          heat == null ? ' is-neutral' : ''
                        }`}
                        data-testid={`studio-customize-heat-${id}`}
                        data-heat={heat == null ? 'neutral' : heat.toFixed(2)}
                      >
                        <span
                          className="studio-customize-heat-fill"
                          style={
                            heat == null
                              ? undefined
                              : { width: `${Math.round(heat * 100)}%` }
                          }
                        />
                      </span>
                    ) : null}
                    {chip ? (
                      <span
                        className="studio-customize-play-type"
                        data-testid={`studio-customize-play-type-${id}`}
                        data-play-type={chip.id}
                      >
                        {chip.label}
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className={`studio-customize-timeline-handle is-start ${
                      atStart || (edgeHit === 'start' && activeId === id)
                        ? 'at-bound'
                        : ''
                    }`}
                    aria-label={`Trim start of ${c.label}`}
                    disabled={disabled}
                    data-testid={`studio-customize-trim-start-${id}`}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (disabled) return;
                      onSelectClip(id);
                      startDrag({
                        kind: 'trim',
                        id,
                        edge: 'start',
                        originX: event.clientX,
                        origin: { ...b },
                      });
                    }}
                  />
                  <button
                    type="button"
                    className={`studio-customize-timeline-handle is-end ${
                      atEnd || (edgeHit === 'end' && activeId === id)
                        ? 'at-bound'
                        : ''
                    }`}
                    aria-label={`Trim end of ${c.label}`}
                    disabled={disabled}
                    data-testid={`studio-customize-trim-end-${id}`}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (disabled) return;
                      onSelectClip(id);
                      startDrag({
                        kind: 'trim',
                        id,
                        edge: 'end',
                        originX: event.clientX,
                        origin: { ...b },
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>

          {stackedLanes ? (
            <span
              className="studio-customize-timeline-playhead"
              data-testid="studio-customize-playhead"
              aria-hidden="true"
            />
          ) : null}
          </div>
          {musicEnabled || stackedLanes ? (
            <div
              className="studio-customize-music-lane"
              data-testid="studio-customize-music-lane"
            >
              {musicLabel ? (
                <div
                  className="studio-customize-music-block"
                  data-testid="studio-customize-music-block"
                  data-fade-in={musicFadeInS.toFixed(2)}
                  data-fade-out={musicFadeOutS.toFixed(2)}
                >
                  {showFadeHandles ? (
                    <>
                      <span
                        className="studio-customize-fade-shoulder is-in"
                        style={{ width: `${fadeInPct}%` }}
                        data-testid="studio-customize-fade-in-shoulder"
                      />
                      <span
                        className="studio-customize-fade-shoulder is-out"
                        style={{ width: `${fadeOutPct}%` }}
                        data-testid="studio-customize-fade-out-shoulder"
                      />
                      <button
                        type="button"
                        className="studio-customize-fade-handle is-in"
                        data-testid="studio-customize-fade-in"
                        aria-label="Music fade in"
                        disabled={disabled}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (disabled) return;
                          startDrag({
                            kind: 'fade',
                            edge: 'in',
                            originX: event.clientX,
                            originIn: musicFadeInS,
                            originOut: musicFadeOutS,
                          });
                        }}
                      />
                      <button
                        type="button"
                        className="studio-customize-fade-handle is-out"
                        data-testid="studio-customize-fade-out"
                        aria-label="Music fade out"
                        disabled={disabled}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (disabled) return;
                          startDrag({
                            kind: 'fade',
                            edge: 'out',
                            originX: event.clientX,
                            originIn: musicFadeInS,
                            originOut: musicFadeOutS,
                          });
                        }}
                      />
                    </>
                  ) : null}
                  <span className="studio-customize-music-lane-kicker">
                    {STUDIO_COPY.musicLaneLabel}
                  </span>
                  <span className="studio-customize-music-lane-name truncate">
                    {musicLabel}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="studio-customize-music-empty"
                  data-testid="studio-customize-music-add"
                  disabled={disabled}
                  onClick={() => onMusicLaneClick?.()}
                >
                  {STUDIO_COPY.musicLaneEmpty}
                </button>
              )}
            </div>
          ) : null}
          {stackedLanes ? (
            <div
              className="studio-customize-voice-lane"
              data-testid="studio-customize-voice-lane"
            >
              {voiceLabel ? (
                <div
                  className="studio-customize-voice-span"
                  data-testid="studio-customize-voice-span"
                >
                  <span className="studio-customize-music-lane-kicker">
                    {STUDIO_COPY.recapEditorV2ToolVoice}
                  </span>
                  <span className="studio-customize-music-lane-name truncate">
                    {voiceLabel}
                  </span>
                  <span
                    className="studio-customize-voice-wave"
                    aria-hidden="true"
                  >
                    {Array.from({ length: 16 }, (_, i) => (
                      <i key={i} />
                    ))}
                  </span>
                </div>
              ) : (
                <span className="studio-customize-music-empty">
                  {STUDIO_COPY.recapEditorV2NoVoice}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {edgeHit ? (
        <p
          className="text-xs text-center text-[var(--studio-muted)] mt-1"
          data-testid="studio-customize-edge-bound"
        >
          {STUDIO_COPY.customizeEdgeAtBound}
        </p>
      ) : null}

      {activeId && bounds[activeId] ? (
        <div
          className="flex flex-wrap gap-2 justify-center mt-3"
          data-testid="studio-customize-nudge"
        >
          <button
            type="button"
            className="studio-btn-ghost text-xs"
            disabled={disabled}
            onClick={() => nudge(activeId, 'start', -NUDGE_SEC)}
          >
            {STUDIO_COPY.refineTrimStartEarlier}
          </button>
          <button
            type="button"
            className="studio-btn-ghost text-xs"
            disabled={disabled}
            onClick={() => nudge(activeId, 'start', NUDGE_SEC)}
          >
            {STUDIO_COPY.refineTrimStartLater}
          </button>
          <button
            type="button"
            className="studio-btn-ghost text-xs"
            disabled={disabled}
            onClick={() => nudge(activeId, 'end', -NUDGE_SEC)}
          >
            {STUDIO_COPY.refineTrimEndEarlier}
          </button>
          <button
            type="button"
            className="studio-btn-ghost text-xs"
            disabled={disabled}
            onClick={() => nudge(activeId, 'end', NUDGE_SEC)}
          >
            {STUDIO_COPY.refineTrimEndLater}
          </button>
          {stackedLanes ? (
            <>
              <button
                type="button"
                className="studio-btn-ghost text-xs"
                disabled={disabled}
                data-testid="studio-customize-move-left"
                onClick={() =>
                  moveClip(orderedIds.indexOf(activeId), -1)
                }
              >
                Move left
              </button>
              <button
                type="button"
                className="studio-btn-ghost text-xs"
                disabled={disabled}
                data-testid="studio-customize-move-right"
                onClick={() =>
                  moveClip(orderedIds.indexOf(activeId), 1)
                }
              >
                Move right
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CustomizeTimeline;
